import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { IonicModule, Platform, IonPopover } from '@ionic/angular';
import { addIcons } from 'ionicons';
import { timeOutline, calendarOutline } from 'ionicons/icons';


// import { SopiDividerComponent } from '../divider/divider.component';
// import { LineStyleEnum } from '../../enums/line-style.enum';
// import { SopiTextDirective } from '../../directives/text.directive';
import { CommonModule } from '@angular/common';

type DateValue = string | number | Date | null | undefined;

// Temporary inline enum replacement until LineStyleEnum is available
type LineStyleEnum = 'full' | 'inset' | 'none';

/**
 * `SopiDatePickerComponent` kann Uhrzeit und Datum anzeigen und bearbeiten
 *
 * `SopiDatePickerComponent` can display and edit the time and date
 */

@Component({
  selector: 'sopi-date-picker',
  templateUrl: './sopi-date-picker.component.html',
  styleUrls: ['./sopi-date-picker.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    IonicModule,
    // SopiDividerComponent, // TODO: Add when available
    // SopiTextDirective, // TODO: Add when available
  ],
  encapsulation: ViewEncapsulation.Emulated,
})
export class SopiDatePickerComponent implements OnInit, OnChanges {
  private readonly platform = inject(Platform);

  // ViewChild für Time Popover
  @ViewChild('timePopover') timePopover?: IonPopover;

  // INPUT

  /**
   * Ein eindeutiger Identifier, der für die Zuordnung der Ionic Komponenten benötigt wird (ion-datetime-button zu ion-datetime)
   *
   * A unique identifier that is required for the assignment of the Ionic components (ion-datetime-button to ion-datetime)
   * @default null
   */
  @Input() id: string | null = null;

  /**
   * Gibt an, ob das Feld nur zur Anzeige von Werten benutzt wird.
   *
   * Specifies whether the field is only used to display values.
   * @default false
   */
  @Input() isReadOnly = false;

  /**
   * Gibt an, ob die Komponente in der Desktop (true) oder Mobile (false) Version angezeigt wird
   *
   * Specifies whether the component is displayed in the desktop (true) or mobile (false) version
   * @default desktop
   */
  @Input() isDesktop: boolean | null = null;

  /**
   * Vorbelegtes Datum im Format 'yyyy-MM-dd'
   *
   * Pre-assigned date in the format 'yyyy-MM-dd'
   * @default null
   */
  @Input() currentDate: string | null | undefined = null;

  /**
   * Vorbelegte Zeit im Format 'HH:mm'
   *
   * Preset time in the format 'HH:mm'
   * @default null
   */
  @Input() currentTime: string | null | undefined = null;

  /**
   * Gibt an, ob das Datum nur angezeigt (false) oder auch geändert werden kann (true)
   *
   * Specifies whether the date can only be displayed (false) or can also be changed (true)
   * @default true
   */
  @Input() dateEnabled = true;

  /**
   * Gibt an, ob das komplette Datum Auswahl angezeigt werden soll (true) oder nicht (false)
   *
   * Specifies whether the complete date selection should be displayed (true) or not (false)
   * @default true
   */
  @Input() dateVisible = true;

  /**
   * Gibt an, ob die Zeit nur angezeigt (false) oder auch geändert werden kann (true)
   *
   * Specifies whether the time can only be displayed (false) or can also be changed (true)
   * @default true
   */
  @Input() timeEnabled = true;

  /**
   * Gibt an, ob das komplette Zeit Auswahl angezeigt werden soll (true) oder nicht (false)
   *
   * Specifies whether the complete time selection should be displayed (true) or not (false)
   * @default true
   */
  @Input() timeVisible = true;

  /**
   * Man braucht den Hinweis-Text nicht immer und kann in hiermit deaktivieren
   *
   * You don't always need the hint text and can deactivate it here
   * @default true
   */
  @Input() showHint = true;

  /**
   * Hint-Text: Wird an oberste Stelle als Hinweis dargestellt für die Hinweismeldung als Header-Text
   *
   * Hint text: Is displayed at the top as a hint for the hint message as header text
   * @default null
   */
  @Input() hinttext: string | null = null;

  /**
   * Titel-Text: Hier wird an zentraler Stelle, in Fett und in großer Schrift dargestellt. Wird auch verwendet für die Hinweismeldung als Header-Text
   *
   * Title text: This is displayed in a central position, in bold and in large font. Also used for the information message as header text
   * @default null
   */
  @Input() label: string | null = null;

  /**
   * Die optionale Zusatz-Beschriftung des Items
   *
   * The optional additional labeling of the item
   * @default null
   */
  @Input() sublabel: string | null = null;

  /**
   * Wie die Trennlinie gezeichnet werden soll: "full" ｜ "inset" ｜ "none"
   *
   * How the dividing line should be drawn: "full" ｜ "inset" ｜ "none"
   * @default Inset
   */
  @Input() lines: LineStyleEnum = 'inset';

  /**
   * Padding am Ende (für die iOS-Version)
   *
   * Padding at the end (for the iOS version)
   * @default '0'
   */
  @Input() paddingEnd = '0';

  /**
   * Erlaubte Wochentage (0 = So, 1 = Mo … 6 = Sa).
   * Beispiel: `[1,3,5]` lässt Montag, Mittwoch, Freitag zu.
   * `null` oder leeres Array ⇒ keine Einschränkung.
   *
   * Allowed weekdays (0 = Sun, 1 = Mon … 6 = Sat).
   * Example: `[1,3,5]` allows Monday, Wednesday, Friday.
   * `null` or empty array ⇒ no restriction.
   * @default null
   */
  @Input() allowedWeekdays: number[] | null = null;

  // OUTPUT
  /**
   * Gibt das aktuelle Datum aus
   *
   * Outputs the current date
   */
  @Output() outChanged: EventEmitter<string> = new EventEmitter<string>();

  // LOCAL

  date: string | null | undefined = '';
  time: string | null | undefined = '';

  // Flag um zu verhindern, dass updateFromInputs() während eines Events emittiert
  private isProcessingEvent = false;

  // Computed property: ISO-String mit lokalem Timezone-Offset für ion-datetime
  // Damit ion-datetime die Zeit korrekt anzeigt, insbesondere bei presentation="time"
  get currentDateTimeWithOffset(): string {
    if (!this.currentDateTime) {
      return new Date().toISOString();
    }
    
    // Konvertiere UTC-ISO-String zu lokalem ISO-String mit Timezone-Offset
    const utcDate = new Date(this.currentDateTime);
    const localIsoString = this.toLocalISOString(utcDate);
    return localIsoString;
  }

  // Hilfsfunktion: Konvertiert ein Date-Objekt zu einem ISO-String mit lokalem Timezone-Offset
  // Beispiel: "2026-01-22T15:30:00+01:00" statt "2026-01-22T14:30:00.000Z"
  private toLocalISOString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    // Timezone-Offset in Minuten
    const timezoneOffset = date.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
    const offsetMinutes = Math.abs(timezoneOffset) % 60;
    const offsetSign = timezoneOffset <= 0 ? '+' : '-';
    const offsetString = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
    
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetString}`;
  }


  // Speichert das vorherige Datum für Richtungserkennung bei Tastatur/Mausrad Navigation
  private previousDate: string | null | undefined = '';

  currentDateTime: string = new Date().toISOString();

  // Zeitoptionen für die Zeitauswahl-Liste (00:00 - 23:55 in 5-Minuten-Schritten)
  timeOptions: string[] = [];

  // Prüft, ob ein Datum für die Auswahl zugelassen ist (via allowedWeekdays)
  dateEnabledFn = (date: DateValue): boolean => {
    const allowed = this.allowedWeekdays;
    if (!allowed || allowed?.length === 0) {
      return true; // keine Einschränkung
    }
    const d = new Date(date as string);
    return allowed.includes(d.getDay());
  }

  constructor() {
    addIcons({ timeOutline, calendarOutline });

    // Generiere Zeitoptionen von 00:00 bis 23:55 in 5-Minuten-Schritten
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 5) {
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        this.timeOptions.push(`${hourStr}:${minuteStr}`);
      }
    }
  }

  ngOnInit() {
    // Set default for isDesktop if not provided
    this.isDesktop ??= this.platform.is('desktop');

    // Initialize date and time from inputs
    this.updateFromInputs();
  }

  ngOnChanges(changes: SimpleChanges) {
    // React to changes in currentDate or currentTime
    // Aber nicht während wir ein Event von ion-datetime verarbeiten
    if (!this.isProcessingEvent && (changes['currentDate'] || changes['currentTime'])) {
      this.updateFromInputs();
    }
  }


  private updateFromInputs() {
    const _currentDate = this.currentDate;
    const _currentTime = this.currentTime;

    if (
      _currentDate !== null &&
      _currentDate !== undefined &&
      _currentDate !== '' &&
      _currentTime !== null &&
      _currentTime !== undefined &&
      _currentTime !== ''
    ) {
      // currentDate und currentTime kommen von der date pipe und sind in LOKALER Zeit
      // Wir müssen sie zu UTC konvertieren
      const localDate = new Date(
        `${_currentDate}T${_currentTime}:00`
      );
      this.currentDateTime = localDate.toISOString();
    }

    this.date = _currentDate;
    this.time = _currentTime;
  }

  // Desktop Popover Methoden
  selectDate(event: CustomEvent) {
    this.dateChanged(event, 'date');
    // Aktualisiere auch das manuelle Input-Feld
    const dateIsoString = event.detail.value;
    if (dateIsoString) {
      const [datePart] = dateIsoString.split('T');
      this.date = datePart;
    }
  }

  selectTime(event: CustomEvent) {
    this.dateChanged(event, 'time');
    // Aktualisiere auch das manuelle Input-Feld
    const dateIsoString = event.detail.value;
    if (dateIsoString) {
      const [, timePart] = dateIsoString.split('T');
      this.time = timePart.slice(0, 5);
    }
  }

  // Wählt eine Zeit aus der Liste aus
  async selectTimeFromList(timeString: string, popover: IonPopover) {
    this.isProcessingEvent = true;

    const currentDate = this.date || new Date().toISOString().split('T')[0];

    // Behandle die ausgewählte Zeit als LOKALE Zeit (wie bei manueller Eingabe)
    // und konvertiere sie zu UTC
    const localDate = new Date(`${currentDate}T${timeString}:00`);
    const isoString = localDate.toISOString();

    // Aktualisiere interne Werte
    this.time = timeString;
    this.currentDateTime = isoString;

    // Emittiere Änderung
    this.outChanged.emit(isoString);

    // Schließe Popover
    await this.onPopoverDismiss(popover);

    setTimeout(() => {
      this.isProcessingEvent = false;
    }, 0);
  }

  // Wird aufgerufen, wenn das Time-Popover geöffnet wird
  onTimePopoverDidPresent() {
    if (!this.time) {
      return;
    }

    // Finde die nächstgelegene Zeit im 5-Minuten-Raster
    const nearestTime = this.roundToNearest5Minutes(this.time);

    // Warte kurz, bis das Popover vollständig gerendert ist
    setTimeout(() => {
      // Suche nach dem Item mit der nächstgelegenen Zeit
      const allItems = document.querySelectorAll('.time-select-list ion-item');

      allItems.forEach((item: any) => {
        const label = item.querySelector('ion-label');
        if (label && label.textContent?.trim() === nearestTime) {
          item.scrollIntoView({
            behavior: 'auto', // Sofortiges Scrollen ohne Animation
            block: 'center'
          });
        }
      });
    }, 50);
  }

  // Rundet eine Zeit auf das nächste 5-Minuten-Intervall
  private roundToNearest5Minutes(timeString: string): string {
    const [hours, minutes] = timeString.split(':').map(Number);

    // Runde Minuten auf das nächste 5er-Intervall
    const roundedMinutes = Math.round(minutes / 5) * 5;

    // Handle Überlauf (z.B. 23:58 -> 00:00)
    let finalHours = hours;
    let finalMinutes = roundedMinutes;

    if (finalMinutes >= 60) {
      finalMinutes = 0;
      finalHours = (finalHours + 1) % 24;
    }

    return `${String(finalHours).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}`;
  }

  // Explizite Popover-Management-Methoden
  async closeAllPopovers() {
    try {
      // Schließe alle offenen Popovers explizit
      const popovers = document.querySelectorAll('ion-popover');
      const popoverArray = Array.from(popovers);
      for (const popover of popoverArray) {
        const ionPopover = popover as HTMLIonPopoverElement;
        if (ionPopover.isOpen) {
          await ionPopover.dismiss();
        }
      }
    } catch (error: unknown) {
      console.warn('Fehler beim Schließen der Popovers:', error);
    }
  }

  async onPopoverDismiss(popover: IonPopover) {
    try {
      if (popover?.isOpen) {
        await popover.dismiss();
      }
      // Zusätzliche Bereinigung
      setTimeout(() => {
        const overlays = document.querySelectorAll(
          'ion-popover-overlay, .popover-backdrop'
        );
        overlays.forEach((overlay) => overlay.remove());
      }, 100);
    } catch (error: unknown) {
      console.warn('Fehler beim Schließen des Popovers:', error);
    }
  }


  // Verbesserte Bestätigungsmethoden für Desktop-Popovers
  confirmDateSelection(popover: IonPopover) {
    this.onPopoverDismiss(popover);
  }

  confirmTimeSelection(popover: IonPopover) {
    this.onPopoverDismiss(popover);
  }

  // Formatiert Datum für die Desktop-Anzeige (tt.mm.jjjj)
  formatDateForDisplay(dateString: string | null | undefined): string {
    if (!dateString) {
      return '';
    }

    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch {
      return dateString;
    }
  }

  dateChanged(event: CustomEvent, changeType?: 'date' | 'time') {
    // Setze Flag, um zu verhindern, dass ngOnChanges während der Verarbeitung emittiert
    this.isProcessingEvent = true;

    const dateIsoString = event.detail.value;

    // ion-datetime gibt einen ISO-String zurück:
    // - Mit Timezone-Offset: "2026-01-22T11:00:00+01:00" (lokale Zeit)
    // - Oder UTC: "2026-01-22T10:00:00.000Z"
    //
    // Wir müssen ihn korrekt zu UTC konvertieren:
    // "2026-01-22T11:00:00+01:00" -> parse als Date -> toISOString() -> "2026-01-22T10:00:00.000Z"

    let normalizedIsoString: string;

    // Parse den ISO-String als Date-Objekt (berücksichtigt automatisch den Timezone-Offset)
    // und konvertiere zu UTC
    const dateObj = new Date(dateIsoString);
    normalizedIsoString = dateObj.toISOString();

    const [datePart, timePart] = normalizedIsoString.split('T');
    const formattedDate = datePart;
    const formattedTime = timePart.slice(0, 5); // '14:05'

    // Speichere das ursprüngliche currentDateTime VOR der Überschreibung
    const previousDateTime = this.currentDateTime;
    const originalPresetTime = this.currentTime;

    if (this.dateVisible && !this.timeVisible) {
      // Wenn nur Datum sichtbar ist und eine vorbelegte Zeit existiert, verwende diese
      let timeToUse = formattedTime || '00:00';
      if (
        !this.timeVisible &&
        originalPresetTime &&
        originalPresetTime.length > 0
      ) {
        timeToUse = originalPresetTime;
      }

      // Erstelle lokales Datum und konvertiere zu ISO (UTC)
      const localDate = new Date(`${formattedDate}T${timeToUse}:00`);
      const finalIsoString = localDate.toISOString();

      // Aktualisiere die internen Werte NACH der Emission
      this.currentDateTime = finalIsoString;
      this.date = formattedDate;
      this.time = timeToUse;

      this.outChanged.emit(finalIsoString);
    } else {
      // Wenn explizit nur das Datum geändert wurde, behalte die ursprüngliche UTC-Zeit bei
      if (changeType === 'date' && previousDateTime) {
        // Nur Datum wurde geändert
        // Extrahiere die Zeit-Komponente (HH:mm:ss) aus dem ursprünglichen UTC-String
        const previousUtcTime = previousDateTime.split('T')[1]; // z.B. "16:30:00.000Z"
        // Kombiniere das neue Datum mit der ursprünglichen UTC-Zeit
        const newIsoString = `${formattedDate}T${previousUtcTime}`;

        // Aktualisiere die Anzeige-Zeit (extrahiere lokale Zeit aus dem neuen ISO-String)
        const displayDate = new Date(newIsoString);
        const displayTime = `${String(displayDate.getHours()).padStart(2, '0')}:${String(displayDate.getMinutes()).padStart(2, '0')}`;

        // Aktualisiere die internen Werte NACH der Berechnung
        this.currentDateTime = newIsoString;
        this.date = formattedDate;
        this.time = displayTime;

        this.outChanged.emit(newIsoString);
      } else {
        // Zeit wurde geändert oder beide oder unbekannt
        // Verwende den normalisierten ISO-String

        // Aktualisiere die internen Werte NACH der Normalisierung
        this.currentDateTime = normalizedIsoString;
        this.date = formattedDate;
        this.time = formattedTime;

        this.outChanged.emit(normalizedIsoString);
      }
    }

    // Setze Flag zurück nach einem kurzen Timeout, um sicherzustellen,
    // dass alle Change Detection Cycles abgeschlossen sind
    setTimeout(() => {
      this.isProcessingEvent = false;
    }, 0);
  }

  changeDate($event: Event) {
    const target = $event.target as HTMLInputElement;
    if (target?.value) {
      this.date = target.value;
      this.createDate();
    }
  }

  changeTime($event: CustomEvent) {
    // Mobile ion-datetime gibt einen CustomEvent mit ISO-String in detail.value zurück
    // Behandle es genauso wie dateChanged()
    this.dateChanged($event, 'time');
  }

  // Event-Handler für manuelle Eingaben
  onDateInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target?.value) {
      // Nur speichern, KEINE sofortige Validation bei direkter Eingabe
      // Dies ermöglicht zweistellige Eingaben wie "12" ohne vorzeitige Korrektur
      this.date = target.value;
      this.syncToDateTime();
    }
  }

  onDateBlur(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target?.value) {
      // Erst Jahreskorrektur anwenden, dann Wochentag-Validation
      const yearCorrectedDate = this.correctYearInput(target.value);
      if (yearCorrectedDate !== target.value) {
        target.value = yearCorrectedDate;
      }

      // Validation nur beim Verlassen des Feldes, immer vorwärts suchend
      const correctedDate = this.validateAndCorrectDate(
        yearCorrectedDate,
        target,
        false
      );
      this.date = correctedDate;
      this.createDate();
    }
  }

  // Behandelt Tastatur-Navigation (Pfeiltasten) mit bidirektionaler Validation
  onDateKeyDown(event: KeyboardEvent) {
    // Nur bei Pfeiltasten hoch/runter validieren
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      // Kleine Verzögerung um sicherzustellen, dass der neue Wert gesetzt wurde
      setTimeout(() => {
        const target = event.target as HTMLInputElement;
        if (target?.value) {
          const correctedDate = this.validateAndCorrectDate(
            target.value,
            target,
            true
          );
          this.date = correctedDate;
          this.syncToDateTime();
        }
      }, 50);
    }
  }

  onTimeInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target?.value) {
      this.time = target.value;
      this.updateTimeOnly(); // Spezielle Behandlung für Zeit-Updates
      this.syncToDateTime();
    } else if (target?.value === '') {
      // Leeres Zeit-Feld handling
      this.time = '';
      this.syncToDateTime();
    }
  }

  onTimeBlur(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target?.value) {
      // Runde die eingegebene Zeit auf das nächste 5-Minuten-Intervall
      const roundedTime = this.roundToNearest5Minutes(target.value);

      // Aktualisiere das Input-Feld mit der gerundeten Zeit
      if (roundedTime !== target.value) {
        target.value = roundedTime;
        this.time = roundedTime;
      }

      this.createDate();
    }
  }

  // Synchronisiert manuelle Eingaben mit dem currentDateTime für die Ionic Picker
  private syncToDateTime() {
    let currentDate = this.date;
    const currentTime = this.time || '00:00';

    // Wenn kein Datum gesetzt ist, verwende das aktuelle Datum aus currentDateTime oder heute
    if (!currentDate) {
      if (this.currentDateTime) {
        const utcDate = new Date(this.currentDateTime);
        const year = utcDate.getFullYear();
        const month = String(utcDate.getMonth() + 1).padStart(2, '0');
        const day = String(utcDate.getDate()).padStart(2, '0');
        currentDate = `${year}-${month}-${day}`;
      } else {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        currentDate = `${year}-${month}-${day}`;
      }
    }

    // Erstelle lokales Datum und konvertiere zu UTC ISO
    try {
      const localDate = new Date(`${currentDate}T${currentTime}:00`);
      this.currentDateTime = localDate.toISOString();
    } catch (error) {
      console.warn('Fehler beim Parsen des Datums/Zeit:', error);
      // Fallback: Setze auf aktuelles Datum/Zeit
      this.currentDateTime = new Date().toISOString();
    }
  }

  /**
   * Validiert ein Datum gegen allowedWeekdays und korrigiert es falls nötig
   * @param dateString Das zu validierende Datum im Format 'yyyy-MM-dd'
   * @param inputElement Das HTML input Element (wird aktualisiert falls korrigiert)
   * @param useDirectionDetection Ob Richtungserkennung verwendet werden soll (true für Tastatur-Navigation, false für direkte Eingabe)
   * @returns Das validierte/korrigierte Datum
   */
  private validateAndCorrectDate(
    dateString: string,
    inputElement?: HTMLInputElement,
    useDirectionDetection = true
  ): string {
    // Keine Validation wenn allowedWeekdays nicht gesetzt oder leer ist
    const allowedDays = this.allowedWeekdays;
    if (!allowedDays || allowedDays?.length === 0) {
      // Speichere das Datum als "vorheriges" für nächste Navigation
      if (useDirectionDetection) {
        this.previousDate = dateString;
      }
      return dateString;
    }

    // Prüfe ob das Datum bereits erlaubt ist
    if (this.dateEnabledFn(dateString)) {
      // Speichere das Datum als "vorheriges" für nächste Navigation
      if (useDirectionDetection) {
        this.previousDate = dateString;
      }
      return dateString;
    }

    let correctedDate: string;

    if (useDirectionDetection) {
      // Erkenne Navigationsrichtung anhand des vorherigen Datums (für Tastatur/Mausrad)
      const previousDate = this.previousDate;
      const isNavigatingBackward = this.isNavigatingBackward(
        previousDate,
        dateString
      );

      correctedDate = isNavigatingBackward
        ? this.findPreviousAllowedDate(dateString)
        : this.findNextAllowedDate(dateString);
    } else {
      // Für direkte Eingaben immer vorwärts suchen (User will meist ein bestimmtes Datum erreichen)
      correctedDate = this.findNextAllowedDate(dateString);
    }

    // Aktualisiere das HTML input Element falls vorhanden
    if (inputElement && correctedDate !== dateString) {
      // Setze das korrigierte Datum im Input-Element
      setTimeout(() => {
        inputElement.value = correctedDate;
      }, 0);
    }

    // Speichere das korrigierte Datum als "vorheriges" für nächste Navigation
    if (useDirectionDetection) {
      this.previousDate = correctedDate;
    }
    return correctedDate;
  }

  /**
   * Findet das nächste erlaubte Datum ab dem gegebenen Datum
   * @param startDateString Startdatum im Format 'yyyy-MM-dd'
   * @returns Das nächste erlaubte Datum
   */
  private findNextAllowedDate(startDateString: string): string {
    try {
      const checkDate = new Date(startDateString + 'T00:00:00.000Z');

      // Maximal 14 Tage vorwärts suchen (2 Wochen sollten immer ein erlaubtes Datum enthalten)
      for (let i = 0; i < 14; i++) {
        const checkDateString = checkDate.toISOString().split('T')[0];

        if (this.dateEnabledFn(checkDateString)) {
          return checkDateString;
        }

        // Einen Tag hinzufügen
        checkDate.setUTCDate(checkDate.getUTCDate() + 1);
      }

      // Fallback: Wenn kein erlaubtes Datum gefunden wurde, gib das ursprüngliche zurück
      console.warn('Kein erlaubtes Datum in den nächsten 14 Tagen gefunden');
      return startDateString;
    } catch (error) {
      console.warn('Fehler beim Suchen des nächsten erlaubten Datums:', error);
      return startDateString;
    }
  }

  /**
   * Findet das vorherige erlaubte Datum vor dem gegebenen Datum
   * @param startDateString Startdatum im Format 'yyyy-MM-dd'
   * @returns Das vorherige erlaubte Datum
   */
  private findPreviousAllowedDate(startDateString: string): string {
    try {
      const checkDate = new Date(startDateString + 'T00:00:00.000Z');

      // Maximal 14 Tage rückwärts suchen (2 Wochen sollten immer ein erlaubtes Datum enthalten)
      for (let i = 0; i < 14; i++) {
        const checkDateString = checkDate.toISOString().split('T')[0];

        if (this.dateEnabledFn(checkDateString)) {
          return checkDateString;
        }

        // Einen Tag zurück
        checkDate.setUTCDate(checkDate.getUTCDate() - 1);
      }

      // Fallback: Wenn kein erlaubtes Datum gefunden wurde, gib das ursprüngliche zurück
      console.warn('Kein erlaubtes Datum in den vorherigen 14 Tagen gefunden');
      return startDateString;
    } catch (error) {
      console.warn(
        'Fehler beim Suchen des vorherigen erlaubten Datums:',
        error
      );
      return startDateString;
    }
  }

  /**
   * Bestimmt ob der User rückwärts navigiert (z.B. mit Pfeiltasten oder Mausrad)
   * @param previousDate Das vorherige Datum
   * @param currentDate Das aktuelle Datum
   * @returns true wenn rückwärts navigiert wird
   */
  private isNavigatingBackward(
    previousDate: string | null | undefined,
    currentDate: string
  ): boolean {
    if (!previousDate) {
      return false; // Keine vorherige Navigation bekannt, nehme Vorwärts an
    }

    try {
      const prevDate = new Date(previousDate + 'T00:00:00.000Z');
      const currDate = new Date(currentDate + 'T00:00:00.000Z');

      return currDate < prevDate;
    } catch (error) {
      console.warn(
        'Fehler beim Vergleichen der Daten für Richtungserkennung:',
        error
      );
      return false; // Bei Fehlern nehme Vorwärts an
    }
  }

  createDate() {
    if (this.isDesktop) {
      if (this.dateVisible) {
        const dateStr = this.date;
        if (dateStr && dateStr.length > 0) {
          // Prüfe zuerst, ob eine vorbelegte Zeit aus currentTime existiert
          const originalPresetTime = this.currentTime;
          let timeToUse = '00:00';

          if (
            !this.timeVisible &&
            originalPresetTime &&
            originalPresetTime.length > 0
          ) {
            timeToUse = originalPresetTime;
          } else if (this.time && (this.time ?? '')?.length > 0) {
            timeToUse = this.time;
          }

          // Erstelle lokales Datum und konvertiere zu UTC ISO
          const localDate = new Date(`${dateStr}T${timeToUse}:00`);
          this.outChanged.emit(localDate.toISOString());
        }
      } else if (!this.dateVisible && this.timeVisible) {
        const dateStr = this.date;
        const timeStr = this.time;

        if (dateStr && dateStr.length > 0 && timeStr && timeStr.length > 0) {
          // Erstelle lokales Datum und konvertiere zu UTC ISO
          const localDate = new Date(`${dateStr}T${timeStr}:00`);
          this.outChanged.emit(localDate.toISOString());
        } else if (timeStr && timeStr.length > 0) {
          // Wenn kein Datum vorhanden ist, verwende heutiges Datum
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const localDate = new Date(`${year}-${month}-${day}T${timeStr}:00`);
          this.outChanged.emit(localDate.toISOString());
        }
      }
    } else {
      const newDate = this.time ?? this.date ?? null;
      if (newDate !== null) {
        // Für Mobile: Wenn bereits ein ISO-String, direkt verwenden
        if (newDate.includes('T')) {
          this.outChanged.emit(newDate);
        } else {
          // Ansonsten als lokales Datum interpretieren und zu UTC konvertieren
          const localDate = new Date(`${newDate}T00:00:00`);
          this.outChanged.emit(localDate.toISOString());
        }
      }
    }
  }

  // Speziell für Zeit-Updates ohne Datum
  private updateTimeOnly() {
    if (!this.dateVisible && this.timeVisible) {
      const currentTime = this.time;
      if (currentTime) {
        // Verwende das aktuelle Datum aus currentDateTime oder heute
        let currentDate: string;
        if (this.currentDateTime) {
          const utcDate = new Date(this.currentDateTime);
          const year = utcDate.getFullYear();
          const month = String(utcDate.getMonth() + 1).padStart(2, '0');
          const day = String(utcDate.getDate()).padStart(2, '0');
          currentDate = `${year}-${month}-${day}`;
        } else {
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          currentDate = `${year}-${month}-${day}`;
        }

        // Erstelle lokales Datum und konvertiere zu UTC ISO
        const localDate = new Date(`${currentDate}T${currentTime}:00`);
        this.currentDateTime = localDate.toISOString();
      }
    }
  }

  /**
   * Korrigiert zweistellige Jahreszahlen zu vierstelligen
   * Logik: 00-30 → 2000-2030, 31-99 → 1931-1999
   * @param dateString Das Datum im Format 'yyyy-MM-dd'
   * @returns Das korrigierte Datum
   */
  private correctYearInput(dateString: string): string {
    try {
      const parts = dateString.split('-');
      if (parts.length !== 3) {
        return dateString; // Ungültiges Format, keine Korrektur
      }

      let year = parts[0];
      const month = parts[1];
      const day = parts[2];

      // Prüfe ob es ein 1-4 stelliges Jahr ist, das korrigiert werden sollte
      const yearNum = Number.parseInt(year, 10);

      if (yearNum >= 0 && yearNum <= 99) {
        // Zweistellige Jahre intelligenter zuordnen
        if (yearNum >= 0 && yearNum <= 30) {
          year = (2000 + yearNum).toString(); // 00-30 → 2000-2030
        } else {
          year = (1900 + yearNum).toString(); // 31-99 → 1931-1999
        }

        return `${year}-${month}-${day}`;
      }

      // Für Jahre zwischen 100 und 999 auch korrigieren (vermutlich Tippfehler)
      if (yearNum >= 100 && yearNum <= 999) {
        year = (2000 + (yearNum % 100)).toString();
        return `${year}-${month}-${day}`;
      }

      return dateString; // Bereits vierstellig oder außerhalb des Korrekturbereichs
    } catch (error) {
      console.warn('Fehler bei der Jahreskorrektur:', error);
      return dateString; // Bei Fehlern ursprünglichen Wert zurückgeben
    }
  }
}

